/**
 * One learning path — its courses, in order, and where the learner stands in it.
 *
 * Built to the prototype's `#screen-program-detail`, whose own spec describes three states
 * on one page (`data-prog-state` = browse | enrolled | completed). Those are the three
 * states below, driven by `LearningPathView.state` instead of a DOM attribute.
 *
 * The page reuses the COURSE page's furniture — `.course__hero`, `.course__grid`, the
 * sticky `.course__side` action card, `.course__section` — because a path and a course are
 * the same kind of object to a reader: a thing you read about and then decide to start.
 * Giving them two different layouts would make the second one feel like a different site.
 *
 * WHAT IS NOT HERE, AND WHY. The prototype's detail page also carries a mentors panel, a
 * path representative with Message and Book a call buttons, and a certificate preview:
 *
 *   mentors / rep      no data source. Open edX has course teams, not path mentors
 *
 * THE PRICE AND THE "SAVE VS BUYING SEPARATELY" LINE ARE NOW REAL, AND THIS COMMENT USED TO
 * SAY THEY NEVER WOULD BE. It said the number would be invented because there was no
 * path-level SKU and no checkout. Both exist now: `openedx_thinlms_pricing` holds a bundle
 * price per pathway and computes the buy-separately comparison server-side, so every figure
 * in the status card below is the server's and none is derived here. The old copy was deleted
 * only once that was true — the same rule the certificate paragraph below records.
 *
 * THE CERTIFICATE SECTION IS NOW REAL, AND IT USED TO SAY SO WHEN IT WAS NOT. This page
 * carried, in this comment and in the completed state of the status card, a plain statement
 * that this academy awards no certificate for a learning path — because at the time nothing
 * issued one and a certificate card would have been a promise with no mechanism behind it.
 * There is a mechanism now: `openedx_thinlms_pathways` writes a `PathwayCertificate` when a
 * learner holds a passing course certificate for every step, and `PathwayCertificateSection`
 * below reads it. The old copy was deleted only once that was true end to end.
 *
 * THE SECTION'S STATE COMES FROM THE SERVER, NOT FROM `view.state`. `paths.ts` computes
 * completion in the browser out of per-course progress that returns ZERO on any error, so
 * the page's own idea of "finished" is right for a progress bar and must never be what
 * decides whether somebody holds a credential. The progress bar and the step rows keep
 * using it; the certificate section asks `/api/pathways/v1/paths/<key>/certificate`.
 *
 * The grading criteria are real FIELDS and were never real RULES, so "How this path is
 * completed" no longer renders from them — see `Completion` below. The page states the one
 * rule this app actually applies: finish every course.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Banner, Button, Icon, Spinner } from '../../shared/ui';
import { getConfig, text } from '../../shared/lib/config';
import { courseHref } from '../../shared/lib/catalogue';
import { loadSession, signIn } from '../../shared/lib/session';
import VerifyAccountNotice, { useAccountVerified } from '../VerifyAccountNotice';
import { credentialId, formatCertificateDate } from '../../shared/lib/certificate-view';
import {
  getMyPathwayCertificate,
  pathwayCertificateHref,
  type MyPathwayCertificate,
} from '../../shared/lib/pathway-certificates';
import {
  beginCheckout,
  buyState,
  explainPricingFailure,
  pathwayPriceView,
  PRICE_COPY,
  priceLabel,
  type BuyState,
  type PathwayPriceView,
} from '../../shared/lib/pricing';
import { usePathwayPrice } from '../../shared/lib/usePricing';
import { PriceBlock, SeparatelyNote, UnavailableNotice } from '../../shared/ui/price';
import {
  joinedCleanly,
  joinLearningPath,
  listLearningPaths,
  pathHref,
  pct,
  PATH_SEAM_KEY,
  PATHS_ARE_FIXTURES,
  PATHS_REQUIRE_SIGN_IN,
  type LearningPathView,
  type PathJoinResult,
  type PathStepView,
} from '../../shared/lib/paths';
import './learner.css';
import './paths.css';

export default function PathDetail() {
  const { pathSlug = '' } = useParams();

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const signedIn = session.data?.authenticated === true;

  // THE SAME QUERY KEY AS /paths AND THE DASHBOARD, on purpose — the same reasoning the
  // dashboard already writes down for sharing it with the browse page.
  //
  // It used to be its own key, `['learning-path', slug, signedIn]`, because the detail page
  // called a DIFFERENT endpoint: upstream's list omits `description` and `subtitle`, so the
  // page had to re-fetch one path to get them. `/api/pathways/v1/learner/paths` carries them
  // for every path, so `getLearningPath` now reads that same list — and giving it the list's
  // own key is what stops React Query fetching the list a second time under a private name.
  //
  // MEASURED: clicking a card on /paths cost one `learner/paths` request before this line and
  // none after it. The list is not re-requested at all; the page renders from the cache the
  // grid already filled. `PATH_SEAM_KEY` is exported by `paths.ts` so the three call sites
  // cannot drift apart — a fourth page with a typo in this array would silently pay again.
  const path = useQuery({
    queryKey: PATH_SEAM_KEY(signedIn),
    queryFn: () => listLearningPaths(signedIn),
    enabled: !session.isPending,
    retry: false,
    select: (views) => views.find((view) => view.path.slug === pathSlug) ?? null,
  });

  if (session.isPending || path.isPending) {
    return (
      <main className="course">
        <Spinner label="Loading this learning path…" />
      </main>
    );
  }

  // Checked BEFORE "missing", because for a signed-out visitor the two are the same result
  // and different facts: the pathways API is authenticated-only, so it has told us nothing
  // about whether this path exists. Saying "we could not find it" would be a guess.
  if (!signedIn && PATHS_REQUIRE_SIGN_IN) return <PathUnavailable reason="signed_out" />;
  if (path.isError) return <PathUnavailable reason="error" />;
  if (!path.data) return <PathUnavailable reason="missing" />;

  const view = path.data;

  return <PathBody view={view} signedIn={signedIn} />;
}

/**
 * The page, once the path has resolved.
 *
 * Split out only so the certificate query can be keyed on a path key that is known to
 * exist. A hook cannot be called conditionally, and asking for a certificate for a pathway
 * this academy does not have would be a request whose only possible answer is 404.
 */
function PathBody({ view, signedIn }: { view: LearningPathView; signedIn: boolean }) {
  /**
   * DO I HOLD A CERTIFICATE FOR THIS PATHWAY?
   *
   * Its own query, deliberately not folded into `getLearningPath`: it is the ONE thing on
   * this page the browser must not compute, and keeping it a separate request keeps that
   * visible. It is also the call that performs the lazy top-up server-side — a learner who
   * finished while this was half-deployed is issued their certificate by opening this page.
   *
   * `retry: false` and never fatal. A failed check must not blank a page whose real subject
   * is the courses; the section below renders an honest "could not check" instead.
   */
  const certificate = useQuery({
    queryKey: ['pathway-certificate', view.path.key],
    queryFn: () => getMyPathwayCertificate(view.path.key),
    enabled: signedIn,
    retry: false,
    // The answer changes exactly once in a learner's life — when they finish — and the
    // course pages invalidate it on completion. Refetching on every focus would re-run the
    // server-side award check for no reason.
    staleTime: 5 * 60_000,
  });

  /**
   * THIS PATH'S OWN PRICE — A BATCH OF ONE (spec R6.9, R7.1's scale note).
   *
   * `usePathwayPrice` asks about this pathway alone, so a cold landing on a path URL costs
   * one small request and never depends on the /paths grid's payload being in cache. That is
   * exactly the property `PATH_SEAM_KEY` gave up when it made the detail page free by
   * requiring the whole collection first, and it is why the pricing seam is keyed per
   * screenful rather than per tenant.
   *
   * The key does not carry `signedIn`. A price is the same integer for everybody — the
   * payload has no per-learner field — so keying on a session flag that is `false` until it
   * resolves would refetch the same numbers on every cold load, which is the live suspect
   * this file's own `PATH_SEAM_KEY` comment describes.
   */
  const prices = usePathwayPrice(view.path.key);
  const price = pathwayPriceView(prices, view.path.key);

  return (
    <main className="course">
      <Breadcrumb title={view.path.title} />

      <PathHero view={view} />

      {PATHS_ARE_FIXTURES && (
        <Banner tone="info">
          <strong>This learning path is sample data.</strong> The pathways backend is not
          installed on this academy yet. The courses below are real and can be opened and
          enrolled in; the path itself, its sequence and any progress shown against it are
          examples.
        </Banner>
      )}

      <div className="course__grid">
        <div>
          <About view={view} />
          <Steps view={view} />
          <Completion view={view} />
          <PathwayCertificateSection view={view} certificate={certificate} />
        </div>

        <aside className="course__side">
          <StatusCard
            view={view}
            certificate={certificate.data ?? null}
            price={price}
            signedIn={signedIn}
          />
        </aside>
      </div>
    </main>
  );
}

/* --------------------------------------------------------------- breadcrumb ----- */

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="breadcrumb">
        <li className="breadcrumb-item">
          <Link to="/paths">{text('nav_paths', 'Learning Paths')}</Link>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          {title}
        </li>
      </ol>
    </nav>
  );
}

/* --------------------------------------------------------------------- hero ----- */

function PathHero({ view }: { view: LearningPathView }) {
  const { path, stepCount } = view;
  const [artFailed, setArtFailed] = useState(false);

  return (
    <header className="course__hero">
      {/* `heroImage`, NOT `image`. The card art a path borrows from its first course has
          that course's title typeset into the pixels, and full-bleed behind this `<h1>` it
          rendered as a grey watermark across the path's own name. */}
      {path.heroImage && !artFailed && (
        <img
          src={path.heroImage}
          alt=""
          aria-hidden="true"
          className="course__hero-art"
          onError={() => setArtFailed(true)}
        />
      )}

      <div className="course__hero-body">
        <Link to="/paths" className="course__back">
          <Icon name="arrow_back" />
          All learning paths
        </Link>

        <div>
          {/* Singular, and deliberately NOT the `nav_paths` copy key — that key is the
              navigation label ("Learning Paths") and reusing it here labels one path with
              a plural. */}
          <span className="course__org">
            <Icon name="route" />
            Learning path
          </span>
        </div>

        {path.invite_only && (
          <div>
            <span className="visibility-chip is-private" title="Invitation-only learning path">
              <Icon name="lock" />
              Private · invite only
            </span>
          </div>
        )}

        <h1>{path.title}</h1>
        <p className="course__tagline">{path.description}</p>

        <div className="course__hero-meta">
          <span>
            <Icon name="menu_book" />
            {stepCount} {stepCount === 1 ? 'course' : 'courses'}
          </span>
          <span className="dot-sep">·</span>
          <span>
            <Icon name={path.sequential ? 'linear_scale' : 'apps'} />
            {/* "Recommended", not "Taken in order": nothing enforces the sequence. */}
            {path.sequential ? 'Recommended order' : 'Take in any order'}
          </span>
          {view.state !== 'not_enrolled' && (
            <>
              <span className="dot-sep">·</span>
              <span>
                <Icon name="donut_large" />
                {pct(view.progress)}% complete
              </span>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------- about ----- */

function About({ view }: { view: LearningPathView }) {
  return (
    <section className="course__section">
      <h2>About this learning path</h2>
      <p className="course__prose">{view.path.description}</p>
    </section>
  );
}

/* -------------------------------------------------------------------- steps ----- */

/**
 * The courses in the path, in order.
 *
 * The ordered list is the whole page — everything else is framing. Each row carries its
 * position, what state the learner is in on it, and one action, and the action is a real
 * link to a real course page in every state, including the ones further down the sequence.
 */
function Steps({ view }: { view: LearningPathView }) {
  const { path, steps } = view;

  return (
    <section className="course__section">
      <h2>Courses in this learning path</h2>

      {/* Only shown when it is TRUE of this path, and only claiming what is true of it.
          It used to read "each course opens when the one before it is complete", which is
          a restriction this product does not have: the ordering is drawn by this page and
          a learner who opens step 3 first gets step 3. So it says what the order IS —
          the authors' recommendation — rather than what it would enforce if it could. */}
      {path.sequential && (
        <div className="curriculum-legend">
          <span>
            <Icon name="linear_scale" />
            The academy recommends taking these courses in this order. You can open any of
            them at any time.
          </span>
        </div>
      )}

      <ol className="path-steps">
        {steps.map((step) => (
          <Step key={`${step.order}-${step.courseKey}`} step={step} />
        ))}
      </ol>
    </section>
  );
}

const STEP_PILL: Record<PathStepView['state'], { label: string; icon: string }> = {
  completed: { label: 'Completed', icon: 'check' },
  in_progress: { label: 'In progress', icon: 'play_arrow' },
  available: { label: 'Available', icon: 'radio_button_unchecked' },
  // Was "Locked", with a padlock. It describes where the course sits in the suggested
  // order, which is all this app knows — it is not a permission and must not look like one.
  later: { label: 'Later in the sequence', icon: 'schedule' },
};

function Step({ step }: { step: PathStepView }) {
  const pill = STEP_PILL[step.state];

  return (
    <li className="path-step" data-state={step.state}>
      {/* Every state except "finished" shows its POSITION. A padlock here was the page's
          loudest claim that the course was shut, and it was never shut. */}
      <div className="path-step__index" aria-hidden="true">
        {step.state === 'completed' ? <Icon name="check" /> : step.order}
      </div>

      <div className="path-step__body">
        <h3>{step.title}</h3>

        <div className="path-step__meta">
          <span className="path-step__pill" data-state={step.state}>
            <Icon name={pill.icon} />
            {pill.label}
          </span>
          {step.effort && <span>{step.effort}</span>}
          {/* Weight is the path's own arithmetic and only worth stating when it is not the
              default — otherwise every row would carry a "counts once" that means nothing. */}
          {step.weight > 1 && <span>Counts {step.weight}×</span>}
        </div>

        {step.summary && <p className="path-step__summary">{step.summary}</p>}

        {step.state === 'in_progress' && (
          <div className="path-bar path-bar--slim">
            <div className="path-bar__fill" style={{ width: `${pct(step.completion)}%` }} />
          </div>
        )}

        {/* A step whose course this academy cannot see. It keeps its row — dropping it
            would make the path claim fewer courses than it has — and says why it has no
            link rather than rendering a link that 404s. */}
        {!step.linked && (
          <p className="path-step__unlinked">
            <Icon name="info" />
            This course is not available in this academy.
          </p>
        )}
      </div>

      <div className="path-step__action">
        <StepAction step={step} />
      </div>
    </li>
  );
}

/**
 * One action per row, and it is a real link in EVERY state.
 *
 * The `later` state used to render a disabled "Locked" button. That button was the product
 * telling a learner they could not do something they could do — the course page was one
 * pasted URL away, and this page's own Discover grid links to it. A disabled control that
 * a second route contradicts is worse than no control.
 */
function StepAction({ step }: { step: PathStepView }) {
  if (!step.linked) return null;

  const label =
    step.state === 'completed' ? 'Review' : step.state === 'in_progress' ? 'Continue' : 'View course';

  return (
    <Link
      to={courseHref(step.courseKey)}
      className="ui-btn"
      data-variant={step.state === 'in_progress' ? 'primary' : 'secondary'}
    >
      {label}
    </Link>
  );
}

/* --------------------------------------------------------------- completion ----- */

/**
 * What actually finishes the path — one rule, and it is the one this app can check.
 *
 * WHAT THIS SECTION USED TO SAY, AND WHY IT IS GONE. It printed the plugin's stored
 * `required_completion` and `required_grade`: "Finish 2 of 2 courses (80% of the path)" and
 * "Reach at least 75% in each course". The first sentence disagreed with itself — two
 * numbers describing the same requirement, one of them the ceiling of 0.8 × 2 and the other
 * the raw fraction. The second was simply not true: nothing in this stack compares a grade
 * to `required_grade`, and the page went on to declare the path complete for a learner
 * sitting at 0.02.
 *
 * So there is one bullet, it names every course, and `assembleView` decides completion by
 * exactly the same rule. A page cannot state a promise the code beside it does not keep.
 */
function Completion({ view }: { view: LearningPathView }) {
  return (
    <section className="course__section">
      <h2>How this learning path is completed</h2>
      <ul className="course__learn">
        <li>
          Finish {view.stepCount === 1 ? 'the course' : `all ${view.stepCount} courses`} in the
          path. Each one counts as complete when you have finished its content.
        </li>
        {view.path.sequential && (
          <li>
            They are listed in the order the academy recommends, but you can take them in any
            order.
          </li>
        )}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------ pathway certificate ----- */

/**
 * The bottom section: what finishing this learning path is worth, and whether you have it.
 *
 * This is the block the prototype drew as one card with a "Preview" button that did nothing
 * and looked identical in all three of its states. It has five states here, and each one is
 * a different fact rather than a different word:
 *
 *   could not check   the request failed. Says so. Never guesses, in either direction.
 *   not joined        what the credential IS and what it takes — the browse case
 *   in progress       how many courses remain, against the page's own progress bar
 *   being prepared    finished, no credential yet. The honest transient.
 *   earned            the seal, the issue date, the credential id, and the link
 *
 * "BEING PREPARED" IS A REAL STATE, NOT A HEDGE. The award runs inside the request that
 * saved the last unit, so it is normally already there by the time this page loads. It can
 * legitimately lag: a step course that awards no certificate at all (no active certificate
 * configuration) means the pathway can never complete server-side while the browser's own
 * progress bar reads 100%, and this section must not claim otherwise. Opening the page runs
 * the server-side check again, so if it is issuable it will be issued.
 */
function PathwayCertificateSection({
  view,
  certificate,
}: {
  view: LearningPathView;
  certificate: UseQueryResult<MyPathwayCertificate | null>;
}) {
  const academy = getConfig().tenant.name;
  const { stepCount, stepsCompleted, state } = view;
  const remaining = Math.max(stepCount - stepsCompleted, 0);
  const held = certificate.data ?? null;

  return (
    <section className="course__section">
      <h2>Learning path certificate</h2>

      {held ? (
        <EarnedCertificateCard certificate={held} academy={academy} />
      ) : (
        <div className="path-cert" data-state={certificate.isError ? 'unknown' : state}>
          <span className="path-cert__seal" aria-hidden="true">
            <Icon name="workspace_premium" />
          </span>
          <div className="path-cert__body">
            <h3>
              {view.path.title} · {academy}
            </h3>
            <p>
              {certificate.isError ? (
                <>
                  We could not check your certificate for this learning path just now.
                  Nothing has been lost — reload the page to try again.
                </>
              ) : state === 'completed' ? (
                <>
                  <strong>Your certificate is being prepared.</strong> You have finished
                  every course here. If it does not appear shortly, one of the courses in
                  this path may not award a certificate of its own — ask the academy.
                </>
              ) : state === 'not_enrolled' ? (
                <>
                  Finish {stepCount === 1 ? 'the course' : `all ${stepCount} courses`} in
                  this learning path to earn the <strong>{view.path.title}</strong>{' '}
                  certificate from {academy}. It is verified, has its own link, and can be
                  shared with anyone.
                </>
              ) : (
                <>
                  <strong>
                    {stepsCompleted} of {stepCount} courses finished.
                  </strong>{' '}
                  {remaining === 0
                    ? 'Your certificate is being prepared.'
                    : `Complete the remaining ${remaining} to earn your certificate.`}
                </>
              )}
            </p>

            {/* The same bar the status card draws, repeated here only where it says
                something: on a path nobody has joined there is no progress to show, and a
                bar sitting at zero reads as a stalled one. */}
            {state !== 'not_enrolled' && !certificate.isError && (
              <div className="path-bar path-bar--slim">
                <div className="path-bar__fill" style={{ width: `${pct(view.progress)}%` }} />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** The earned state — the document's own details, and the way to it. */
function EarnedCertificateCard({
  certificate,
  academy,
}: {
  certificate: MyPathwayCertificate;
  academy: string;
}) {
  const issued = formatCertificateDate(certificate.issued);
  return (
    <div className="path-cert" data-state="earned">
      <span className="path-cert__seal" aria-hidden="true">
        <Icon name="workspace_premium" />
      </span>
      <div className="path-cert__body">
        <h3>
          {certificate.pathway.title} · {certificate.pathway.academy_name || academy}
          <span className="path-cert__chip">
            <Icon name="verified" />
            Earned
          </span>
        </h3>
        <p>
          {issued ? `Issued ${issued}. ` : ''}
          Credential <span className="path-cert__id">{credentialId(certificate.uuid)}</span>
        </p>
        <p className="path-cert__note">
          {/* Says what the link IS, because the next thing most people do with it is send
              it to somebody who has no account here. */}
          Anyone with the link can check this certificate — no sign-in needed.
        </p>
      </div>
      <div className="path-cert__action">
        <Link
          to={pathwayCertificateHref(certificate.uuid)}
          className="ui-btn"
          data-variant="primary"
        >
          <Icon name="workspace_premium" />
          View certificate
        </Link>
        {/* The same real download as everywhere else: open the document, then the browser's
            own print-to-PDF. Never a button that does nothing. */}
        <Link
          to={`${pathwayCertificateHref(certificate.uuid)}?print=1`}
          className="ui-btn"
          data-variant="secondary"
        >
          <Icon name="download" />
          Download PDF
        </Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- status card ----- */

/**
 * The sticky card, in the prototype's three states.
 *
 * THE BROWSE STATE'S PRIMARY ACTION IS "JOIN THIS LEARNING PATH", and getting here took
 * removing a note that was false. The card used to open the first course instead, under a
 * line reading "Enrolling in the whole path at once needs the pathways backend". The
 * backend was already installed and already shipped enrolment; what was missing was any
 * call to it. Nothing in this app had ever written a path enrolment, every progress element
 * on every pathway surface is gated on `enrollment_date`, and so a learner could finish
 * every course in a path and watch the page never move.
 *
 * `joinLearningPath` is that call — upstream's own `POST /{key}/enrollments/`, followed by
 * upstream's own per-course `POST /{key}/enrollments/{course_key}/` for each step. The
 * button reports what actually happened, including the half-failure case, and can be
 * pressed again safely; see the mutation below and the function's own notes.
 *
 * THE COMPLETED STATE USED TO SAY "THIS ACADEMY DOES NOT AWARD A CERTIFICATE FOR A LEARNING
 * PATH ITSELF", and that was true when it was written: nothing issued one. It is no longer
 * true, so the sentence is gone and the card links to the credential instead — but only
 * when the SERVER says one exists. A learner who has finished every course and whose
 * certificate has not been written yet is sent to their course certificates, exactly as
 * before, rather than to a link that would 404.
 */
function StatusCard({
  view,
  certificate,
  price,
  signedIn,
}: {
  view: LearningPathView;
  certificate: MyPathwayCertificate | null;
  /** Already resolved by the page, so this card never decides what a price means. */
  price: PathwayPriceView;
  signedIn: boolean;
}) {
  const { state, steps, stepCount, stepsCompleted, progress } = view;
  const client = useQueryClient();
  const [outcome, setOutcome] = useState<PathJoinResult | null>(null);

  const join = useMutation({
    mutationFn: () => joinLearningPath(view),
    onSuccess: async (result) => {
      setOutcome(result);
      // The page is entirely derived from these queries, so this is what turns the card
      // into the enrolled state, fills the bar, and lights up the dashboard panel and the
      // course pages' "part of" block on the way back. Awaited, so the card does not stop
      // saying "Working…" before the numbers it is about to show have arrived.
      //
      // THE LAST TWO ARE NOT PATHWAY QUERIES, and they matter as much as the first three:
      // joining enrolled the learner in N COURSES, so the dashboard's course library
      // (`learner-home`/`my-courses`) is now stale in a way that has nothing to do with
      // paths. Leaving them would show somebody a filled pathway bar above a shelf that
      // does not yet contain the courses it counted.
      await Promise.all([
        client.invalidateQueries({ queryKey: ['learning-path'] }),
        client.invalidateQueries({ queryKey: ['learning-paths'] }),
        client.invalidateQueries({ queryKey: ['course-paths'] }),
        client.invalidateQueries({ queryKey: ['learner-home'] }),
        client.invalidateQueries({ queryKey: ['my-courses'] }),
      ]);
    },
  });

  // Where the learner should go next: the first thing not finished. Null on a completed
  // path, and on a path whose every step is unresolvable in this academy.
  const next = steps.find((step) => step.state === 'in_progress' && step.linked)
    ?? steps.find((step) => step.linked && step.state !== 'completed')
    ?? null;

  /**
   * BUYING THE BUNDLE — one Product at one Price, never a basket of its courses (spec D2).
   *
   * The browser names the pathway and a return path and sends NO AMOUNT: what is charged is
   * the Stripe Price the payments backend synced, so a number a browser could edit is
   * neither sent nor trusted (R2.5). No promotion code is collected — the learner types one
   * into Stripe's own field on Stripe's own page (D4, R5.9).
   *
   * `window.location.assign` rather than a router navigation, because the destination is
   * Stripe's origin. A failure stays on this card with the server's own sentence, and
   * nothing has been charged: checkout had not started.
   */
  const checkout = useMutation({
    mutationFn: () =>
      beginCheckout({
        kind: 'pathway',
        key: view.path.key,
        returnPath: pathHref(view.path.slug),
      }),
    onSuccess: (session) => window.location.assign(session.checkout_url),
  });

  /**
   * The six states of R6.10, resolved once by `pricing.ts` and only rendered here.
   *
   * `owned` is "in the path at all", which is what a pathway enrolment means — the same fact
   * the progress bar above is gated on. Ownership OUTRANKS a broken payment account, so a
   * learner who already joined keeps seeing Continue even during a pricing outage; that is
   * the rule that stops an outage locking paying learners out of what they hold.
   *
   * `paymentPending` is not passed: it is the payments backend's fact, and inferring it from
   * a missing enrolment would tell somebody their payment is processing when it has failed.
   */
  const buy: BuyState = buyState({ view: price, signedIn, owned: state !== 'not_enrolled' });

  if (state === 'completed') {
    return (
      <div className="course__card">
        <div className="course__card-body">
          <p className="path-status__done">
            <Icon name="verified" />
            You have completed this learning path
          </p>
          <p className="path-status__rule">
            All {stepCount} {stepCount === 1 ? 'course' : 'courses'} finished.
          </p>

          {certificate ? (
            <>
              <Link
                to={pathwayCertificateHref(certificate.uuid)}
                className="ui-btn course__cta"
                data-variant="primary"
              >
                <Icon name="workspace_premium" />
                View certificate
              </Link>
              <p className="course__card-note">
                <Icon name="info" />
                Your learning path certificate has been issued. It has its own link, which
                anyone can open without signing in.
              </p>
            </>
          ) : (
            <>
              <Link to="/certificates" className="ui-btn course__cta" data-variant="primary">
                <Icon name="workspace_premium" />
                My certificates
              </Link>
              <p className="course__card-note">
                <Icon name="info" />
                Your learning path certificate is being prepared. The certificates you
                earned in its courses are already listed there.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="course__card">
      <div className="course__card-body">
        {state === 'not_enrolled' ? (
          <p className="path-status__title">Start this learning path</p>
        ) : (
          <>
            <p className="path-status__title">Your progress</p>
            <div className="path-progress__row">
              <span>
                {stepsCompleted} of {stepCount} complete
              </span>
              <strong>{pct(progress)}%</strong>
            </div>
            <div className="path-bar">
              <div className="path-bar__fill" style={{ width: `${pct(progress)}%` }} />
            </div>
          </>
        )}

        {/*
          THE PRICE BLOCK, ABOVE THE RULE LINE AND ONLY FOR SOMEBODY WHO DOES NOT HOLD THE
          PATH (spec R6.9).

          `PriceBlock` renders nothing at all for a FREE path — the "Start this learning
          path" title and the join button are the whole statement — renders the amount, the
          struck buy-separately comparison and R5.4's processing disclosure for a paid one,
          and renders "Price unavailable" with the server's reason when the total could not be
          computed. `SeparatelyNote` then states the buy-separately total IN WORDS for the case
          `PriceBlock` cannot strike through: a `fixed` bundle priced above its parts gets no
          strike and no saving, and the learner is still entitled to know what the parts cost
          (R5.5, AC-011).
        */}
        {buy !== 'enter' && (
          <>
            <PriceBlock view={price} />
            {price.wasMinor === null && (
              <SeparatelyNote separateMinor={price.separateMinor} currency={price.currency} />
            )}
          </>
        )}

        <p className="path-status__rule">
          Finish {stepCount === 1 ? 'the course' : `all ${stepCount} courses`} to complete the
          path.
        </p>

        {buy === 'unavailable' ? (
          /*
            NO BUTTON, A STATED REASON, AND THE STEP LIST LEFT INTACT (R6.9 NO-STRIPE).
            The courses are still listed and still readable — a learner may want to know
            what is in a path they cannot buy today. When the reason is
            `membership_unreadable` the seam has already turned it into the sentence that
            says the courses could not be read, so nothing is paraphrased here.
          */
          <UnavailableNotice reason={price.reason} fallback={PRICE_COPY.cannotBuyPathway} />
        ) : buy === 'pending' ? (
          <>
            <Button variant="primary" disabled busy>
              Payment pending
            </Button>
            <p className="course__card-note">
              <Icon name="schedule" />
              {PRICE_COPY.pending}
            </p>
          </>
        ) : buy === 'sign_in_to_buy' ? (
          <>
            {/* `?buy=1` keeps the intent across the sign-in round trip, the same flag and
                the same shape the course page uses, so one convention covers both. */}
            <Link
              to={`/login?next=${encodeURIComponent(`${pathHref(view.path.slug)}?buy=1`)}`}
              className="ui-btn course__cta"
              data-variant="primary"
            >
              Sign in to buy
            </Link>
            <p className="course__card-note">
              <Icon name="person_add" />
              <span>
                No account yet? <Link to="/register">Create one</Link> — it takes a minute.
              </span>
            </p>
          </>
        ) : buy === 'buy' ? (
          <>
            {/* `ui-btn` repeated for the reason `CourseHome` records: `Button` spreads its
                props after setting its own className, so a className passed in REPLACES it. */}
            <Button
              variant="primary"
              className="ui-btn course__cta"
              busy={checkout.isPending}
              onClick={() => checkout.mutate()}
            >
              {`Buy for ${priceLabel(price)}`}
            </Button>
            {checkout.isError && (
              <Banner tone="danger">
                <strong>Checkout did not open.</strong>{' '}
                {explainPricingFailure(checkout.error)}
              </Banner>
            )}
            {/* The fact that makes the price make sense, and the same sentence the free join
                already carries below — a bundle is worth its number because of what it
                contains. */}
            <p className="course__card-note">
              <Icon name="route" />
              Buying this path enrols you in all {stepCount}{' '}
              {stepCount === 1 ? 'course' : 'courses'} in it.
            </p>
          </>
        ) : state === 'not_enrolled' ? (
          <JoinAction view={view} join={join} outcome={outcome} />
        ) : next ? (
          <Link to={courseHref(next.courseKey)} className="ui-btn course__cta" data-variant="primary">
            <Icon name="play_arrow" />
            Continue
          </Link>
        ) : (
          <Banner tone="info">
            There is nothing to open yet — the courses in this path are not available in this
            academy.
          </Banner>
        )}

        {/* Shown after a join in EITHER state: joining flips the card to "enrolled" on the
            same render, and the learner still has to be told whether their courses all went
            through. */}
        {outcome && <JoinOutcome outcome={outcome} onRetry={() => join.mutate()} busy={join.isPending} />}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- joining ----- */

type JoinMutation = ReturnType<typeof useMutation<PathJoinResult, Error, void>>;

/**
 * The Join button, and the one failure it can have that stops everything.
 *
 * A thrown error here means the PATH enrolment itself was refused, so no course was
 * touched and there is nothing half-done to explain — the learner is exactly where they
 * started and pressing the button again is the whole remedy.
 */
function JoinAction({
  view,
  join,
  outcome,
}: {
  view: LearningPathView;
  join: JoinMutation;
  outcome: PathJoinResult | null;
}) {
  // Same query key the rest of the app uses, so this is a cache read rather than a
  // request. Hooks must run before the early return below, not after it.
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username;
  // Only an explicit `false` blocks — see `useAccountVerified`. A slow or failed lookup
  // must not disable joining for a verified learner.
  const mustVerify = useAccountVerified(username) === false;

  // Already reported on. The outcome block below owns the retry from here.
  if (outcome) return null;

  const count = view.stepCount;

  return (
    <>
      <Button
        variant="primary"
        className="course__cta"
        busy={join.isPending}
        onClick={() => join.mutate()}
        // The server refuses this too — pathway enrolment is a different endpoint from
        // course enrolment and is gated separately. Disabled rather than hidden, with
        // the reason directly below.
        disabled={mustVerify}
      >
        <Icon name="route" />
        Join this learning path
      </Button>

      {mustVerify && username && (
        <VerifyAccountNotice
          username={username}
          variant="inline"
          message="Verify your email address before joining a learning path. We sent you a link when you signed up."
        />
      )}

      {join.isError && (
        <Banner tone="danger">
          <strong>You were not joined to this path.</strong> Nothing was changed, so trying
          again is safe.
        </Banner>
      )}

      <p className="course__card-note">
        <Icon name="info" />
        Joining enrols you in {count === 1 ? 'the course' : `all ${count} courses`} in this
        path, and starts tracking your progress through it.
      </p>
    </>
  );
}

/**
 * What the join actually did, including the case nobody has watched happen.
 *
 * A join is one write for the path and then one per course, so "it worked" and "it failed"
 * are not the only two answers: the learner can end up in the path with some of its courses
 * missing. That state is invisible from the outside — the page flips to enrolled and the
 * bar appears — which is exactly why it is named here rather than inferred from a count
 * later. The retry re-runs the whole join; both upstream calls are idempotent, so a course
 * that already succeeded is untouched and nobody is enrolled twice.
 */
function JoinOutcome({
  outcome,
  onRetry,
  busy,
}: {
  outcome: PathJoinResult;
  onRetry: () => void;
  busy: boolean;
}) {
  const { enrolled, failed, skipped } = outcome;

  if (joinedCleanly(outcome)) {
    return (
      <Banner tone="success">
        <strong>You have joined this learning path.</strong>{' '}
        {enrolled.length > 0 &&
          `You are enrolled in ${enrolled.length} ${enrolled.length === 1 ? 'course' : 'courses'}.`}
        {skipped.length > 0 && (
          <>
            {' '}
            {skipped.length} {skipped.length === 1 ? 'course is' : 'courses are'} not available
            in this academy and {skipped.length === 1 ? 'was' : 'were'} not joined.
          </>
        )}
      </Banner>
    );
  }

  return (
    <Banner tone="warning">
      <strong>
        You have joined the path, but {failed.length} of its{' '}
        {enrolled.length + failed.length} courses could not be joined.
      </strong>{' '}
      You can open the ones that worked from the list on this page.
      <ul className="course__learn">
        {failed.map((step) => (
          <li key={step.courseKey}>
            {step.title} — {step.detail}
          </li>
        ))}
      </ul>
      <div className="cat__banner-action">
        <Button variant="secondary" busy={busy} onClick={onRetry}>
          Try the rest again
        </Button>
      </div>
    </Banner>
  );
}

/* ------------------------------------------------------------- unavailable ----- */

function PathUnavailable({ reason }: { reason: 'missing' | 'error' | 'signed_out' }) {
  if (reason === 'signed_out') {
    return (
      <main className="page page--message">
        <h1>Sign in to see this learning path</h1>
        <p>
          Learning paths are shown to people with an account here, because a path tracks where
          you are in it. Sign in and this link will open the path.
        </p>
        <p>
          <button
            type="button"
            className="ui-btn"
            data-variant="primary"
            onClick={() => signIn()}
          >
            Sign in
          </button>
        </p>
      </main>
    );
  }

  return (
    <main className="page page--message">
      <h1>
        {reason === 'missing'
          ? 'We could not find that learning path'
          : 'That learning path did not load'}
      </h1>
      <p>
        {reason === 'missing'
          ? 'It is not part of this academy. If someone sent you this link, they may have meant a different site.'
          : 'The academy did not answer. Nothing is wrong with your account — please reload the page.'}
      </p>
      <p>
        <Link to="/paths" className="ui-btn" data-variant="primary">
          All learning paths
        </Link>
      </p>
    </main>
  );
}
